
import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FileText, Calendar, QrCode, Image } from "lucide-react";
import { CertificateMetadata, CertificationStatus } from '@/types/certification';
import CertificationProgress from './CertificationProgress';
import CertificateVerification from './CertificateVerification';
import CertificateHistory from './CertificateHistory';
import CertificateTemplateLibrary from './certificate-templates/CertificateTemplateLibrary';
import CertificateTemplateSelector from './CertificateTemplateSelector';
import ProgramSelector from './ProgramSelector';
import { useCertificationLogic } from '@/hooks/student-certification/useCertificationLogic';
import { useCertification } from '@/hooks/useCertification';

interface CertificationsTabProps {
  studentId: number;
  programs: any[];
}

const CertificationsTab = ({ studentId, programs }: CertificationsTabProps) => {
  const [activeTab, setActiveTab] = React.useState<string>('progress');

  const { 
    certificates, 
    progress, 
    criteria,
    isLoading, 
    error,
    issueProgramCertificate, 
    verifyPrerequisites 
  } = useCertification({
    studentId,
    programTitle: null,
    programCategory: null,
  });

  const { 
    selectedProgram,
    setSelectedProgram,
    isTemplateDialogOpen,
    setIsTemplateDialogOpen,
    selectedTemplateType,
    setSelectedTemplateType,
    selectedTemplateId,
    setSelectedTemplateId,
    isProgramCompleted,
    handleIssueCertificate,
    handleConfirmCertificateIssue
  } = useCertificationLogic({
    studentId,
    programs,
    issueProgramCertificate,
    verifyPrerequisites
  });

  // Switch to certificates tab when a new certificate is issued
  useEffect(() => {
    if (certificates && certificates.length > 0 && progress?.certificationStatus === 'issued') {
      setActiveTab('certificates');
    }
  }, [certificates, progress?.certificationStatus]);

  const getStatusBadge = (status?: CertificationStatus) => {
    if (!status) return <Badge variant="outline">Not Started</Badge>;
    
    switch(status) {
      case 'eligible':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">Eligible</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">Pending</Badge>;
      case 'issued':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">Issued</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <ProgramSelector
        programs={programs}
        selectedProgram={selectedProgram}
        onProgramSelect={setSelectedProgram}
        isProgramCompleted={isProgramCompleted}
        onIssueCertificate={handleIssueCertificate}
        progress={progress}
      />

      {progress && (
        <Card className="mb-6">
          <CardHeader className="bg-muted/50">
            <CardTitle className="text-lg flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Certification Status
            </CardTitle>
            <CardDescription>
              {progress.programTitle} ({progress.programCategory})
              {selectedProgram && isProgramCompleted(selectedProgram.split(':')[0]) && 
                <Badge className="ml-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                  Program Completed
                </Badge>
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Status</span>
                <div className="mt-1">{getStatusBadge(progress.certificationStatus)}</div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Achievement Level</span>
                <div className="mt-1 capitalize">
                  {progress.currentLevel ? (
                    <Badge variant="outline" className="capitalize">
                      {progress.currentLevel}
                    </Badge>
                  ) : (
                    <Badge variant="outline">Not Achieved</Badge>
                  )}
                </div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Overall Progress</span>
                <div className="mt-1">
                  <div className="w-full bg-muted rounded-full h-2.5">
                    <div 
                      className="bg-primary h-2.5 rounded-full" 
                      style={{ width: `${progress.overallProgress}%` }}
                    ></div>
                  </div>
                  <span className="text-sm mt-1 inline-block">
                    {Math.round(progress.overallProgress)}%
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="progress" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="progress" className="flex items-center">
            <Calendar className="h-4 w-4 mr-2" />
            Certification Progress
          </TabsTrigger>
          <TabsTrigger value="certificates" className="flex items-center">
            <FileText className="h-4 w-4 mr-2" />
            Issued Certificates
          </TabsTrigger>
          <TabsTrigger value="verification" className="flex items-center">
            <QrCode className="h-4 w-4 mr-2" />
            Verification
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center">
            <Image className="h-4 w-4 mr-2" />
            Templates
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="progress" className="mt-0">
          <CertificationProgress 
            progress={progress} 
            criteria={criteria} 
          />
        </TabsContent>
        
        <TabsContent value="certificates" className="mt-0">
          <CertificateHistory 
            certificates={certificates}
            studentId={studentId} 
          />
        </TabsContent>
        
        <TabsContent value="verification" className="mt-0">
          <CertificateVerification 
            certificates={certificates}
          />
        </TabsContent>
        
        <TabsContent value="templates" className="mt-0">
          <CertificateTemplateLibrary />
        </TabsContent>
      </Tabs>
      
      <CertificateTemplateSelector
        isOpen={isTemplateDialogOpen}
        onClose={() => setIsTemplateDialogOpen(false)}
        onConfirm={handleConfirmCertificateIssue}
        selectedType={selectedTemplateType}
        onSelectType={(type) => setSelectedTemplateType(type)}
        selectedTemplateId={selectedTemplateId}
        onSelectTemplate={(id) => setSelectedTemplateId(id)}
      />
    </div>
  );
};

export default CertificationsTab;
